import React from 'react';
import Select from 'react-select';

/**
 * Componente de seleção de usuário usando react-select.
 * Permite pesquisar e selecionar um usuário de uma lista.
 * 
 * @param {Object} props
 * @param {Array} props.users - Lista de usuários disponíveis [{id, name, nickname?, userName?}]
 * @param {string|number} props.value - ID do usuário selecionado
 * @param {function} props.onChange - Callback quando o usuário é selecionado (recebe o id)
 * @param {string} props.placeholder - Placeholder do campo de busca
 * @param {boolean} props.required - Se o campo é obrigatório
 * @param {boolean} props.disabled - Se o campo está desabilitado
 * @param {string} props.noOptionsMessage - Mensagem quando não há usuários
 */
const UserSelect = ({ 
  users = [], 
  value, 
  onChange, 
  placeholder = "Buscar usuário...",
  required = false,
  disabled = false,
  noOptionsMessage = "Nenhum usuário encontrado"
}) => {
  // Converte usuários para o formato do react-select
  const options = users.map(user => ({
    value: user.id || user.userId,
    label: user.nickname || user.name || user.userName || 'Usuário',
    user: user
  }));

  // Encontra a opção selecionada
  const selectedOption = options.find(opt => opt.value === value) || null;

  // Handler de mudança
  const handleChange = (option) => {
    onChange(option ? option.value : '');
  };

  // Formata a opção com avatar
  const formatOptionLabel = ({ label, user }) => {
    const initials = label.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--primary-color, #6366f1), var(--secondary-color, #8b5cf6))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: 12,
          fontWeight: 600,
          flexShrink: 0
        }}>
          {initials}
        </div>
        <div>
          <div style={{ fontWeight: 500 }}>{label}</div>
          {user?.nickname && user?.name && user.nickname !== user.name && (
            <div style={{ fontSize: 12, color: 'var(--text-muted, #9ca3af)' }}>
              {user.name || user.userName}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Estilos customizados para o react-select
  const customStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 44,
      borderRadius: 10,
      borderColor: state.isFocused ? 'var(--primary-color, #6366f1)' : 'var(--border-color, #e5e7eb)',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(99, 102, 241, 0.1)' : 'none',
      backgroundColor: 'var(--bg-primary, #fff)',
      '&:hover': {
        borderColor: 'var(--primary-color, #6366f1)'
      }
    }),
    menu: (base) => ({
      ...base,
      borderRadius: 12,
      boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
      border: '1px solid var(--border-color, #e5e7eb)',
      backgroundColor: 'var(--bg-primary, #fff)',
      zIndex: 100,
      overflow: 'hidden'
    }),
    menuList: (base) => ({
      ...base,
      padding: 8
    }),
    option: (base, state) => ({
      ...base,
      borderRadius: 8,
      padding: '10px 12px',
      cursor: 'pointer',
      backgroundColor: state.isSelected 
        ? 'linear-gradient(135deg, rgba(37, 99, 235, 0.15), rgba(29, 78, 216, 0.15))'
        : state.isFocused 
          ? 'var(--bg-secondary, #f3f4f6)' 
          : 'transparent',
      color: state.isSelected ? 'var(--primary-color, #2563EB)' : 'inherit',
      '&:active': {
        backgroundColor: 'var(--bg-secondary, #f3f4f6)'
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: 'var(--text-muted, #9ca3af)'
    }),
    singleValue: (base) => ({
      ...base,
      color: 'var(--text-primary, #1f2937)'
    }),
    input: (base) => ({
      ...base,
      color: 'var(--text-primary, #1f2937)'
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: 'var(--text-muted, #9ca3af)',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease',
      '&:hover': {
        color: 'var(--primary-color, #6366f1)'
      }
    }),
    clearIndicator: (base) => ({
      ...base,
      color: 'var(--text-muted, #9ca3af)',
      '&:hover': {
        color: 'var(--error-color, #ef4444)'
      }
    })
  };

  return (
    <Select
      options={options}
      value={selectedOption}
      onChange={handleChange}
      placeholder={placeholder}
      isDisabled={disabled}
      isClearable={!required}
      isSearchable
      formatOptionLabel={formatOptionLabel}
      styles={customStyles}
      noOptionsMessage={() => noOptionsMessage}
      loadingMessage={() => "Carregando..."}
    />
  );
};

export default UserSelect;
